/**
 * The secret gift pages (Plan 18): the sender's page (`/pages/secret-gift-<batch>`, the
 * packaging QR's landing) and the friend's page (`/pages/gift?t=<token>`, which hands over to
 * the retainer's product page in gift mode, `?view=gift`: sections/product-convert.liquid).
 *
 * Both reach the backbone through account-session.js's door: Shopify's App Proxy on the store
 * (same origin, Shopify vouches for the signed-in customer), the local api with a dev token on
 * 127.0.0.1. The friend's routes are public behind that same door — the token is the
 * capability, like the onboarding link — so the token is moved into this tab's session storage
 * and taken out of the address bar before quiz-gate.js records `location.href` into attribution.
 *
 * Loaded as a plain script after account-session.js (not deferred): Alpine starts in a
 * microtask as soon as its own script runs, so a deferred factory would be defined too late
 * for `x-data`. Same arrangement as the account page.
 */
window.MosesGift = (() => {
  const session = window.accountSession;
  const KEY = 'moses_gift_v1';

  let memory = {};
  const read = () => {
    try {
      return { ...memory, ...JSON.parse(sessionStorage.getItem(KEY) || '{}') };
    } catch {
      return memory;
    }
  };
  const save = (patch) => {
    memory = { ...read(), ...patch };
    try {
      sessionStorage.setItem(KEY, JSON.stringify(memory));
    } catch {
      /* in-memory fallback */
    }
    return memory;
  };

  const stripParams = (names) => {
    const params = new URLSearchParams(location.search);
    let changed = false;
    for (const name of names) if (params.has(name)) { params.delete(name); changed = true; }
    if (!changed) return;
    const search = params.toString();
    try {
      history.replaceState(history.state, '', location.pathname + (search ? `?${search}` : '') + location.hash);
    } catch {
      /* ignore */
    }
  };

  /** This page's address (the token already stripped) with extra parameters, for a return trip. */
  const hereWith = (extra) => {
    const params = new URLSearchParams(location.search);
    for (const [name, value] of Object.entries(extra)) params.set(name, value);
    const search = params.toString();
    return location.pathname + (search ? `?${search}` : '');
  };

  /** The friend's token: once from the URL, then from this tab's storage. */
  function captureToken() {
    const params = new URLSearchParams(location.search);
    const token = params.get('t');
    if (token) {
      if (read().token !== token) memory = {};
      save({ token, resume: null });
    }
    stripParams(['t']);
    return read().token || null;
  }

  const MESSAGES = {
    invalid_email: 'That doesn’t look like an email address.',
    disposable_email: 'We can’t send to a disposable address. Share the link instead.',
    own_email: 'That’s your own address. Send it to a friend.',
    suppressed: 'We can’t email that address. Share the link instead.',
    note_has_link: 'Links aren’t allowed in the note.',
    note_too_long: 'Keep the note under 280 characters.',
    send_limit: 'That’s enough sends for today. Share the link instead.',
    sender_email_missing: 'We need an email address on your account to copy you in. Write to concierge@moses.com.',
    front_unavailable: 'We couldn’t send the email. Try again, or copy the link.',
    gift_email_not_ready: 'Email isn’t available right now. Share the link instead.',
    program_paused: 'The gift program is paused. Try again later.',
    rate_limited: 'Too many requests. Please try again in a minute.',
    http_429: 'Too many requests. Please try again in a minute.',
    gift_not_found: 'This gift link isn’t valid.',
    cart: 'We couldn’t prepare your cart. Please try again.',
  };
  const message = (error) => {
    if (MESSAGES[error?.code]) return MESSAGES[error.code];
    if (error?.detail) return error.detail;
    if (error?.code === 'cart_error') return error.message || MESSAGES.cart;
    return 'Something went wrong. Please try again.';
  };

  const money = (cents, currency = 'USD') => {
    const amount = Number(cents || 0) / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount);
  };
  const dateLong = (iso) =>
    iso ? new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(new Date(iso)) : '';

  const uid = () => {
    try {
      return crypto.randomUUID().replace(/-/g, '');
    } catch {
      return String(Date.now()) + Math.random().toString(36).slice(2, 12);
    }
  };
  const validEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value || '').trim());

  // Shopify's analytics subscribers own delivery and consent (the PostHog custom pixel lists
  // the gift events by name). quiz-form.js wraps the same publisher; either path is fine.
  const track = (name, data = {}) => {
    try {
      if (window.MosesQuizForm?.track) window.MosesQuizForm.track(name, data);
      else window.Shopify?.analytics?.publish?.(name, data);
    } catch {
      /* non-blocking */
    }
  };

  /** The share sheet where there is one, the clipboard otherwise. Returns the channel used. */
  async function share({ title, text, url }) {
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
        return 'share';
      }
    } catch (error) {
      if (error && error.name === 'AbortError') return null;
    }
    await navigator.clipboard.writeText(url);
    return 'copy';
  }

  const json = (body) => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const sender = {
    get: () => session.request('/gift'),
    claim: (source) => session.request('/gift/claim', json({ source })),
    send: (body) => session.request('/gift/send', json(body)),
  };
  const friend = {
    get: (token) => session.request('/gifts/' + encodeURIComponent(token)),
    claim: (token) => session.request('/gifts/' + encodeURIComponent(token) + '/claim', json({})),
  };

  // The cart, by the same Ajax calls cart.js makes (cart.js is deferred; this runs on a click
  // long after, but keeping the calls here means the sequence has no load-order dependency).
  const root = () => (window.__MOSES_QUIZ__?.root || '/');
  async function cartPost(path, body) {
    const response = await fetch(root() + path, json(body));
    let data = {};
    try {
      data = await response.json();
    } catch {
      /* no body */
    }
    if (!response.ok) {
      const error = new Error(data.description || data.message || MESSAGES.cart);
      error.status = response.status;
      error.code = 'cart_error';
      throw error;
    }
    return data;
  }
  const cart = {
    clear: () => cartPost('cart/clear.js', {}),
    add: (items) => cartPost('cart/add.js', { items }),
    update: (body) => cartPost('cart/update.js', body),
    /** Shopify answers the update with `discount_codes`; the code must be there and applicable. */
    applied: (data, code) =>
      (Array.isArray(data?.discount_codes) ? data.discount_codes : []).some(
        (d) => String(d.code || '').toUpperCase() === String(code).toUpperCase() && d.applicable !== false,
      ),
    /** Shopify's own discount link, which sets the code for checkout and comes back here. */
    discountUrl: (code, returnTo) =>
      root() + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(returnTo),
  };

  return {
    session,
    read,
    save,
    captureToken,
    stripParams,
    hereWith,
    message,
    money,
    dateLong,
    uid,
    validEmail,
    track,
    share,
    sender,
    friend,
    cart,
  };
})();

/**
 * The sender's page. `state` mirrors the backbone's (`no_order`, `not_shipped_yet`, `unclaimed`,
 * `ready`, `sent`, `used`, `expired`, `unavailable`, `try_again`) plus the page's own
 * (`loading`, `signed_out`, `paused`, `error`). An `unclaimed` read claims at once: the first
 * eligible visit mints the code, nothing to press.
 */
window.giftSenderPage = function giftSenderPage({ shopifyCustomer, source, friendAmount, senderAmount }) {
  const G = window.MosesGift;
  const onShopify = shopifyCustomer === true || shopifyCustomer === 'true';
  return {
    state: 'loading',
    view: null,
    email: '',
    note: '',
    busy: false,
    error: '',
    copied: false,
    devAccounts: [],
    devAccount: '',

    get signedIn() {
      return G.session.signedIn(onShopify);
    },
    get isLocal() {
      return G.session.isLocal;
    },
    get gift() {
      return this.view?.gift || null;
    },
    get offer() {
      return this.view?.offer || null;
    },
    get friendAmount() {
      return this.offer ? G.money(this.offer.friend_amount_cents, this.offer.currency) : friendAmount;
    },
    get senderAmount() {
      return this.offer ? G.money(this.offer.sender_amount_cents, this.offer.currency) : senderAmount;
    },
    get link() {
      return this.gift?.link || '';
    },
    get expires() {
      return G.dateLong(this.gift?.expires_at);
    },
    get sentTo() {
      return this.gift?.sent?.to || '';
    },
    get sentAt() {
      return G.dateLong(this.gift?.sent?.at);
    },
    get usedAt() {
      return G.dateLong(this.gift?.used?.at);
    },
    get friendName() {
      return this.gift?.used?.friend_first_name || 'Your friend';
    },
    get rewardCopy() {
      const used = this.gift?.used;
      if (!used) return '';
      if (used.reward_status === 'voided') return 'Your friend returned their order, so this ' + this.senderAmount + ' no longer applies.';
      if (used.reward_status === 'consumed') return 'Already applied to your order.';
      if (used.reward_kind === 'renewal_refund') return 'We’ll refund it on your next renewal, the day it bills.';
      // a one-pair sender's reward is a code, emailed the moment it is granted
      if (used.reward_status === 'granted') return 'We’ve emailed you a code to use at your next checkout.';
      return 'We’ll email you a code to use at your next checkout.';
    },

    async init() {
      if (!this.signedIn) {
        this.state = 'signed_out';
        if (this.isLocal) {
          try {
            this.devAccounts = await G.session.devAccounts();
          } catch {
            this.devAccounts = [];
          }
        }
        return;
      }
      await this.load();
    },
    async load() {
      this.state = 'loading';
      this.error = '';
      try {
        this.apply(await G.sender.get());
        if (this.state === 'unclaimed') await this.claim();
        G.track('gift_page_viewed', { state: this.state, batch: source });
      } catch (error) {
        this.fail(error);
      }
    },
    apply(view) {
      this.view = view;
      this.state = view.state;
    },
    async claim() {
      if (this.busy) return;
      this.busy = true;
      this.error = '';
      try {
        this.apply(await G.sender.claim(source));
        G.track('gift_claimed', { state: this.state, batch: source });
      } catch (error) {
        this.fail(error);
      } finally {
        this.busy = false;
      }
    },
    async send(resend = false) {
      if (this.busy) return;
      const to = resend ? this.sentTo : this.email.trim();
      if (!G.validEmail(to)) {
        this.error = 'Enter your friend’s email address.';
        return;
      }
      this.busy = true;
      this.error = '';
      try {
        const body = { friend_email: to, client_token: G.uid() };
        const note = this.note.trim();
        if (!resend && note) body.note = note;
        this.apply(await G.sender.send(body));
        G.track('gift_sent', { channel: 'email', batch: source, resend });
      } catch (error) {
        if (String(error.code || '').startsWith('gift_') && error.code !== 'gift_email_not_ready') {
          await this.load();
        }
        this.error = G.message(error);
      } finally {
        this.busy = false;
      }
    },
    async share() {
      if (!this.link) return;
      const text = `${this.friendAmount} off an Unplastic Retainer, from me. The discount is applied when you open the link.`;
      const channel = await G.share({ title: 'A gift from moses', text, url: this.link });
      if (channel === 'copy') this.flashCopied();
      if (channel) G.track('gift_link_shared', { channel, batch: source });
    },
    async copy() {
      if (!this.link) return;
      try {
        await navigator.clipboard.writeText(this.link);
        this.flashCopied();
        G.track('gift_link_shared', { channel: 'copy', batch: source });
      } catch {
        this.error = 'Copy didn’t work here. Long-press the link to copy it: ' + this.link;
      }
    },
    flashCopied() {
      this.copied = true;
      setTimeout(() => {
        this.copied = false;
      }, 2000);
    },
    fail(error) {
      if (error.code === 'program_paused') this.state = 'paused';
      else if (error.code === 'no_account' || error.code === 'not_signed_in') this.state = 'no_order';
      else if (error.code === 'account_auth_required' && this.isLocal) {
        G.session.signOut();
        this.state = 'signed_out';
      } else this.state = 'error';
      this.error = G.message(error);
    },
    // local only: the dev toolbar's "sign in as", here so the page works on `shopify theme dev`
    async signInAs() {
      if (!this.devAccount) return;
      await G.session.signInAs(this.devAccount);
      await this.load();
    },
  };
};

/**
 * The friend's page. `status` mirrors the backbone's (`valid`, `used`, `expired`, `own_gift`)
 * plus `loading`, `not_found`, `paused`, `error`. The button claims the code, builds the cart
 * (the retainer on the plan with the Travel Case, or one pair), attaches the code and the
 * gift reference, then hands over to the eligibility quiz exactly as the cart's Checkout
 * control does; a qualified tab goes straight to checkout.
 */
window.giftFriendPage = function giftFriendPage(opts) {
  const G = window.MosesGift;
  const defaultFriendCents = 10000;
  return {
    status: 'loading',
    view: null,
    token: null,
    busy: false,
    error: '',

    get offer() {
      return this.view?.offer || null;
    },
    get friendCents() {
      return this.offer?.friend_amount_cents ?? defaultFriendCents;
    },
    get currency() {
      return this.offer?.currency || opts.currency || 'USD';
    },
    get friendAmount() {
      return G.money(this.friendCents, this.currency);
    },
    get senderName() {
      return this.view?.sender_first_name || 'A friend';
    },
    get subscriptionPrice() {
      return G.money(Math.max(0, Number(opts.subscriptionCents || 0) - this.friendCents), this.currency);
    },
    get oneTimePrice() {
      return G.money(Math.max(0, Number(opts.oneTimeCents || 0) - this.friendCents), this.currency);
    },
    /** The plan's full price, which the renewals bill once the gift has been used. */
    get renewalPrice() {
      return G.money(Number(opts.subscriptionCents || 0), this.currency);
    },
    get expires() {
      return G.dateLong(this.view?.expires_at);
    },
    /** Loading or valid: the plans and the button show. Any other status shows why not instead. */
    get showsOffer() {
      return this.status === 'loading' || this.status === 'valid';
    },
    get hasSubscription() {
      return Boolean(opts.sellingPlanId);
    },

    async init() {
      this.token = G.captureToken();
      const resume = G.read().resume;
      G.stripParams(['resume']);
      if (!this.token) {
        this.status = 'not_found';
        return;
      }
      try {
        this.view = await G.friend.get(this.token);
        this.status = this.view.status;
        G.track('gift_opened', { state: this.status });
      } catch (error) {
        this.fail(error);
        return;
      }
      // Back from Shopify's discount link (the Ajax code attach was not honoured): the cart is
      // already built and the code is set for checkout, so carry on to the quiz.
      if (resume && this.status === 'valid') {
        this.busy = true;
        await this.proceed(resume);
      }
    },
    async checkout(plan) {
      if (this.busy || this.status !== 'valid') return;
      if (plan === 'subscription' && !this.hasSubscription) plan = 'one_pair';
      this.busy = true;
      this.error = '';
      try {
        const { code } = await G.friend.claim(this.token);
        const items = [{ id: Number(opts.variantId), quantity: 1 }];
        if (plan === 'subscription') {
          items[0].selling_plan = Number(opts.sellingPlanId);
          if (opts.complimentaryVariantId) {
            // Shopify removes this one-time gift with its subscription parent (cart.js does the same).
            items.push({ id: Number(opts.complimentaryVariantId), quantity: 1, parent_id: Number(opts.variantId) });
          }
        }
        await G.cart.clear();
        await G.cart.add(items);
        const result = await G.cart.update({
          discount: code,
          attributes: { gift_ref: this.view.gift_ref },
        });
        if (!G.cart.applied(result, code)) {
          G.save({ resume: plan });
          location.assign(G.cart.discountUrl(code, G.hereWith({ resume: '1' })));
          return;
        }
        await this.proceed(plan);
      } catch (error) {
        this.busy = false;
        const code = String(error.code || '');
        if (code === 'gift_not_found' || error.status === 404) this.status = 'not_found';
        else if (code.startsWith('gift_')) this.status = code.slice(5);
        else if (code === 'program_paused') this.status = 'paused';
        this.error = G.message(error);
      }
    },
    async proceed(plan) {
      G.save({ resume: null });
      G.track('gift_checkout_started', { plan });
      const quiz = window.MosesQuiz;
      if (quiz && !quiz.qualified()) {
        // The quiz page sends a qualified friend straight to checkout; a held one comes back here.
        quiz.start({ checkout: true, returnUrl: G.hereWith({}) });
        return;
      }
      if (quiz) await quiz.attachCart();
      location.assign((window.__MOSES_QUIZ__?.root || '/') + 'checkout');
    },
    fail(error) {
      if (error.code === 'gift_not_found' || error.status === 404) this.status = 'not_found';
      else if (error.code === 'program_paused') this.status = 'paused';
      else this.status = 'error';
      this.error = G.message(error);
    },
  };
};
